import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { boardService } from "../../../../../lib/api/boards";
import { BoardJournal } from "../../../../../stores/boardStore";
import { SectionLoader } from "../../../../../components/LogoLoader";
import { useAuthStore } from "../../../../../stores/authStore";
import { SafeImage } from "../../../../../components/common/SafeImage";

interface OperationSelectionViewProps {
    journal: BoardJournal;
    selectedOperationIds: string[];
    isAnimating: boolean;
    onBack?: () => void;
    onClose: () => void;
    onToggleOperation: (operationId: string, isSelected: boolean) => void;
    onValidate: () => void;
    viewOnly?: boolean;
    boardCurrency?: string;
    currencyConversions?: Array<{ currency: string; rate: number; manual: boolean }>;
    operationStatuses?: Record<string, boolean>;
}

export const OperationSelectionView: React.FC<OperationSelectionViewProps> = ({
    journal,
    selectedOperationIds,
    isAnimating,
    onBack,
    onClose,
    onToggleOperation,
    onValidate,
    viewOnly = false,
    boardCurrency,
    currencyConversions = [],
    operationStatuses = {},
}) => {
    const { t, i18n } = useTranslation("common");

    const { data: operations = [], isLoading } = useQuery({
        queryKey: ['journal-operations', journal.id],
        queryFn: () => boardService.getGlobalJournalOperations(journal.id),
        enabled: !!journal.id
    });

    // Compute derived state for operations
    const formattedOperations = useMemo(() => {
        return operations.map((op: any) => {
            const currency = op.currency || useAuthStore.getState().user?.currency;
            if (!currency) {
                console.error(`Operation ${op.id} has no currency and no user default found.`);
            }
            return {
                ...op,
                formattedAmount: currency ? new Intl.NumberFormat(i18n.language, {
                    style: 'currency',
                    currency: currency
                }).format(op.amount || 0) : 'ERR',
                date: op.date ? new Date(op.date).toLocaleDateString(i18n.language) : '-'
            };
        });
    }, [operations]);

    // Calculate total value in board currency
    const totalValue = useMemo(() => {
        return operations
            .filter((op: any) => selectedOperationIds.includes(op.id))
            .reduce((sum: number, op: any) => {
                const amount = parseFloat(op.amount || 0);
                const type = (op.type || '').toLowerCase();

                // Convert amount if needed
                let convertedAmount = amount;
                const opCurrency = op.currency || useAuthStore.getState().user?.currency;

                if (boardCurrency && opCurrency && opCurrency !== boardCurrency) {
                    const conversion = currencyConversions.find(c => c.currency === opCurrency);
                    if (conversion && conversion.rate) {
                        convertedAmount = amount / conversion.rate;
                    }
                }

                return type === 'income' ? sum + convertedAmount : sum - convertedAmount;
            }, 0);
    }, [operations, selectedOperationIds, boardCurrency, currencyConversions]);

    const formatValue = (val: number) => {
        return Math.abs(val).toLocaleString(i18n.language, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        });
    };

    const getCurrencySymbol = (code: string) => {
        try {
            return (0).toLocaleString(i18n.language, { style: 'currency', currency: code, minimumFractionDigits: 0, maximumFractionDigits: 0 }).replace(/[\d\s.,]/g, '');
        } catch {
            return '$';
        }
    };

    const symbol = boardCurrency ? getCurrencySymbol(boardCurrency) : "$";

    const handleToggle = (operationId: string) => {
        if (viewOnly) return;
        const isSelected = selectedOperationIds.includes(operationId);
        onToggleOperation(operationId, !isSelected);
    };

    return (
        <>
            {/* Header */}
            <div className="flex-shrink-0 px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 z-10">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        {onBack && (
                            <button
                                onClick={onBack}
                                className="flex-shrink-0 p-1.5 rounded-lg text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-1"
                                disabled={isAnimating}
                            >
                                <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_919272a5.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_919272a5.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                            </button>
                        )}
                        <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-white dark:bg-neutral-800 rounded-full border border-neutral-200 dark:border-neutral-700 overflow-hidden shadow-sm">
                            {(() => {
                                const CDN_URL = (((window as any).__DASHBAL_CONFIG__?.cdnUrl) || 'https://cdn.dashbal.com').replace(/\/$/, '');
                                return (
                                    <SafeImage src={`${CDN_URL}/logos/internal/money_journal.svg`} className="w-6 h-6 object-contain" />
                                );
                            })()}
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col justify-center">
                            <h2 className="text-base font-bold text-neutral-600 dark:text-neutral-100 leading-none mb-0.5 truncate">
                                {journal.name}
                            </h2>
                            <p className="text-xs text-neutral-400 dark:text-neutral-500 font-medium truncate">
                                {viewOnly ? (t("boardJournals.details.viewOnlyInfo") || "View operations included in board") : (t("itemSelection.selectEntitiesInstructions") || "Select operations to include in board")}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => onClose()}
                        className="flex-shrink-0 ml-3 p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors focus:outline-none"
                    >
                        <span className="w-5 h-5 text-neutral-500 dark:text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_4a9e8065.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_4a9e8065.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4" style={{ minHeight: 'calc(85vh - 140px)', maxHeight: 'calc(85vh - 140px)' }}>
                {isLoading ? (
                    <div className="flex justify-center items-center h-full">
                        <SectionLoader size="sm" />
                    </div>
                ) : formattedOperations.length === 0 ? (
                    <div className="text-center text-neutral-400 dark:text-neutral-500 mt-10">
                        {t("boardJournals.details.emptyStateText") || "No operations found"}
                    </div>
                ) : (
                    <div className="space-y-1">
                        {formattedOperations.map((op: any) => {
                            const isSelected = selectedOperationIds.includes(op.id);
                            return (
                                <div
                                    key={op.id}
                                    onClick={() => handleToggle(op.id)}
                                    className={`group flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-all bg-white dark:bg-neutral-900 border-neutral-100 dark:border-neutral-800 hover:border-neutral-200 dark:hover:border-neutral-700 hover:shadow-sm ${operationStatuses[op.id] === false ? 'opacity-60' : ''}`}
                                >
                                    {!viewOnly && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleToggle(op.id);
                                            }}
                                            className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded border border-neutral-200 dark:border-neutral-700 group-hover:border-neutral-300 dark:group-hover:border-neutral-600 bg-white dark:bg-neutral-800 transition-colors"
                                        >
                                            {isSelected && (
                                                <div className="w-3 h-3 rounded-sm bg-accent-primary" />
                                            )}
                                        </button>
                                    )}

                                    {/* Operation Info - matching ChannelsList layout */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start">
                                            <div className="text-sm font-medium truncate text-neutral-700 dark:text-neutral-300">
                                                {op.description || op.category || t('common.unnamed')}
                                            </div>
                                            <div className={`font-mono text-sm flex-shrink-0 ${op.type === 'income' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                                {op.formattedAmount}
                                            </div>
                                        </div>
                                        <div className="text-xs text-neutral-400 dark:text-neutral-500">
                                            <span>{op.displayType || op.type}</span>
                                            <span className="mx-1">•</span>
                                            <span>{op.date}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 px-6 py-4 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/50 rounded-b-2xl">
                <div className="flex justify-between items-center">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-0 sm:gap-1.5 whitespace-nowrap justify-center min-w-0">
                        <span className="text-xs text-neutral-500 dark:text-neutral-400 leading-tight">
                            {selectedOperationIds.length} {t("common.operations")}
                        </span>
                        <span className={`text-xs font-bold leading-tight ${totalValue > 0 ? 'text-emerald-600 dark:text-emerald-400' : totalValue < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-neutral-500 dark:text-neutral-400'}`}>
                            {formatValue(totalValue)} {symbol}
                        </span>
                    </div>
                    <div className="flex gap-2 flex-shrink-0 whitespace-nowrap">
                        <button
                            onClick={() => onBack ? onBack() : onClose()}
                            className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors border border-neutral-200 dark:border-neutral-700"
                        >
                            {viewOnly ? t("actions.back") : t("actions.cancel")}
                        </button>
                        {!viewOnly && (
                            <button
                                onClick={onValidate}
                                className="px-4 py-2 text-sm font-medium text-white bg-accent-primary hover:bg-accent-primary-hover rounded-lg transition-colors flex items-center gap-2"
                            >
                                {t("itemSelection.validate")}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
